/**
 * DocsPage.tsx — /#/docs
 * Documentation site covering setup, posting, equipment, mobile, troubleshooting.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const GOLD = '#c9a84c'
const NAVY = '#0a1628'
const PANEL = '#0f1a2e'

const sections = [
  {
    id: 'getting-started',
    title: '🚀 Getting started',
    articles: [
      {
        title: 'Setting up your organization',
        content: `After signing up and paying, you'll receive a welcome email with two key items:

**Your app URL** — e.g. yourorg.castellan-security.com. Bookmark this and share it with your team. This is the only URL your team needs.

**Your admin call sign** — ADMIN. Use this with the password you set via the setup link in your welcome email to sign in for the first time.

**First steps after signing in:**
1. Go to your app URL and sign in as ADMIN
2. Navigate to Manage Users → create accounts for your supervisors
3. Approve pending registrations as officers join
4. Set up your roster in Manage Roster
5. Start your first shift from the desktop app`,
      },
      {
        title: 'Inviting your team',
        content: `Your welcome email includes an **invite code**. Share this code with your officers and supervisors so they can register.

**How officers register:**
1. Go to your organization's URL
2. Click "Create account"
3. Enter their call sign, name, and invite code
4. Their account will be pending approval
5. A supervisor or admin approves them from Manage Users

**Important:** Keep your invite code private. Only share it with people you want to give access to your organization.`,
      },
      {
        title: 'Roles and permissions',
        content: `Castellan has four roles:

**Developer / Admin** — Full access. Can manage users, configure the organization, and access all features.

**Executive (Director / Assistant Director / Lieutenant)** — Can approve user registrations, view all data, manage shifts, and override assignments.

**Supervisor (Sergeant / OIC)** — Can manage shift assignments, post officers, track equipment, and manage breaks.

**Officer** — Can view their own assignments, check equipment in/out, and view shift details.

Call sign convention: S-01 through S-08 are Supervisors. S-09 and above are Officers.`,
      },
    ],
  },
  {
    id: 'posting',
    title: '📋 Shift & posting',
    articles: [
      {
        title: 'Starting a shift',
        content: `The Posting page is where you set up assignments for the shift.

**To start a shift:**
1. Open the desktop app or web app
2. Navigate to Posting from the sidebar
3. Select the current shift (Morning / Evening / Night)
4. Add officers to the posting board by selecting from the roster
5. Assign each officer to a post (Mall, Vehicle, Desk, Special, Break)
6. Set post times in 30-minute increments
7. Click Push to sync changes to all connected devices

**Shift times:**
- Morning: 07:00 – 15:00
- Evening: 15:00 – 23:00
- Night: 23:00 – 07:00`,
      },
      {
        title: 'Managing breaks',
        content: `**Scheduling breaks:**
1. In the Posting page, click on an officer's card
2. Select "Add break" or open the Equipment Modal
3. Set the break start and end time
4. Assign a coverage officer if needed
5. Push to sync

**Break indicators on the dashboard:**
- Orange border on officer card = currently on break
- Break countdown shows in the officer's card
- 10-minute and 5-minute notifications are sent to the officer's phone before break ends`,
      },
      {
        title: 'Secondary tasks',
        content: `Secondary tasks are assignments beyond the primary post — inspections, escorts, incident responses, and so on.

**Adding a secondary task:**
1. From the Posting page, select an officer
2. Click "Add task"
3. Enter the task description and estimated time
4. Assign to the officer's call sign
5. Push to sync

Tasks appear on the officer's mobile view and on the supervisor dashboard.`,
      },
    ],
  },
  {
    id: 'equipment',
    title: '📻 Equipment tracking',
    articles: [
      {
        title: 'Assigning radios',
        content: `**To assign a radio:**
1. Open the Equipment section on the desktop app
2. Find the radio number in the grid
3. Click the radio card
4. Select the officer from the dropdown
5. The card updates to show the officer's name and blue "Out" status
6. Push to sync all devices

**Radio status colors:**
- Green / Available — radio is in the cabinet, not assigned
- Blue / Out — assigned to an officer currently on duty
- Grey / Stored — assigned but officer is not on duty
- Red / OOS — out of service, not available for assignment`,
      },
      {
        title: 'Marking equipment out of service',
        content: `If a radio, key, recorder, or badge is damaged or non-functional:

1. Click on the equipment card
2. Toggle "Mark as OOS"
3. The card will show a red OOS indicator
4. OOS items appear in the OOS bar at the bottom of the dashboard
5. To restore, click the card again and toggle off

OOS status persists across shifts until manually cleared.`,
      },
      {
        title: 'Security keys and recorders',
        content: `Security keys and body recorders work the same way as radios — assign to an officer, mark as out or available, and track OOS status.

**Keys** are numbered (K-1, K-2, etc.) and tracked by which officer signed them out.

**Recorders** are numbered (#1, #2, etc.) and should be assigned before each shift starts. Officers' recorders are automatically listed on the dashboard alongside their radio and key.`,
      },
    ],
  },
  {
    id: 'mobile',
    title: '📱 Mobile app',
    articles: [
      {
        title: 'Installing on Android',
        content: `**From Google Play:**
1. Open the Google Play Store on your Android device
2. Search for "Castellan Security"
3. Tap Install
4. Open the app and enter your organization's URL when prompted

**Minimum requirements:** Android 8.0 (Oreo) or higher.

**Notifications:** The app will request permission to send notifications. Allow this to receive shift and break end warnings.`,
      },
      {
        title: 'Signing in on mobile',
        content: `1. Open the Castellan app
2. Enter your organization's URL (e.g. yourorg.castellan-security.com)
3. Enter your call sign and password
4. Accept the disclaimer on first sign-in
5. You'll be taken to the mobile command view

The app stays signed in between sessions. You'll be automatically signed out after 10 minutes of inactivity for security.`,
      },
      {
        title: 'Shift and break notifications',
        content: `Castellan sends two types of notifications on Android:

**Shift end warnings:** 10 minutes and 5 minutes before your shift ends.

**Break end warnings:** 10 minutes and 5 minutes before each of your scheduled breaks ends.

Notifications include vibration. Make sure the app has notification permission and that your device is not in Do Not Disturb mode.

If notifications aren't appearing, go to Settings → Apps → Castellan Security → Notifications and ensure they are enabled.`,
      },
    ],
  },
  {
    id: 'troubleshooting',
    title: '🔧 Troubleshooting',
    articles: [
      {
        title: 'Login issues',
        content: `**"Call sign not recognized"**
Your call sign doesn't exist in the system. Check the spelling and capitalization (call signs are uppercase, e.g. S-15). If you're new, you may need to register first or ask your supervisor to verify your account.

**"Incorrect password"**
Your password is wrong. Use "Forgot password?" on the login screen to receive a reset code by email.

**"Account pending approval"**
Your registration is waiting for a supervisor or admin to approve it. Contact your supervisor directly.

**Can't reach the login page**
Make sure you're using the correct URL for your organization (e.g. yourorg.castellan-security.com). The main castellan-security.com is the marketing site, not the app.`,
      },
      {
        title: 'Sync issues',
        content: `**Changes not showing on other devices**
1. Check your internet connection
2. Click the sync/pull button on the desktop app
3. If the issue persists, try a hard refresh (Ctrl+Shift+R on Windows)

**Data from the previous shift is showing**
The shift auto-changes at 07:00, 15:00, and 23:00 ET. If you're seeing old data just after a shift change, pull from cloud to get the latest.

**Offline mode**
Castellan works partially offline — you can view the last synced data. Push and pull require an internet connection.`,
      },
      {
        title: 'Still need help?',
        content: `If you can't find the answer here, contact our support team:

**Email:** support@castellan-security.com

We typically respond within 4 hours during business hours (9 AM – 6 PM ET, Monday – Friday).

**Response time by tier:**
- Shift: Next business day
- Terrain+NFC: Within 4 hours
- Command: Within 1 hour, 24/7`,
      },
    ],
  },
]

export default function DocsPage() {
  const navigate = useNavigate()
  const [activeSection, setActiveSection] = useState('getting-started')
  const [activeArticle, setActiveArticle] = useState(sections[0].articles[0].title)

  const currentSection = sections.find(s => s.id === activeSection) || sections[0]
  const currentArticle = currentSection.articles.find(a => a.title === activeArticle) || currentSection.articles[0]

  function renderContent(text: string) {
    return text.split('\n\n').map((para, i) => {
      const lines = para.split('\n').map((line, j) => {
        const bold = line.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#f1f5f9">$1</strong>')
        return <div key={j} dangerouslySetInnerHTML={{ __html: bold }} style={{ marginBottom: line.startsWith('**') ? 4 : 0 }} />
      })
      return <p key={i} style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: 14, marginBottom: 16 }}>{lines}</p>
    })
  }

  return (
    <div style={{ background: NAVY, color: '#e2e8f0', fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh' }}>
      <nav style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(201,168,76,0.15)', display: 'flex', alignItems: 'center', padding: '0 32px', height: 60, position: 'sticky', top: 0, zIndex: 100 }}>
        <div onClick={() => navigate('/')} style={{ fontSize: 20, fontWeight: 800, color: GOLD, letterSpacing: 2, flex: 1, cursor: 'pointer' }}>CASTELLAN</div>
        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <span onClick={() => navigate('/find-agency')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>Sign in</span>
          <button onClick={() => navigate('/signup')} style={{ padding: '8px 20px', borderRadius: 8, background: GOLD, color: NAVY, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none' }}>Get started</button>
        </div>
      </nav>

      <div style={{ display: 'flex', maxWidth: 1100, margin: '0 auto', padding: '40px 24px', gap: 32 }}>

        {/* Sidebar */}
        <div style={{ width: 220, flexShrink: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', textTransform: 'uppercase' as const, letterSpacing: '.08em', marginBottom: 12 }}>Documentation</div>
          {sections.map(section => (
            <div key={section.id} style={{ marginBottom: 20 }}>
              <div
                onClick={() => { setActiveSection(section.id); setActiveArticle(section.articles[0].title) }}
                style={{ fontSize: 13, fontWeight: 700, color: activeSection === section.id ? GOLD : '#64748b', cursor: 'pointer', marginBottom: 6, padding: '4px 8px', borderRadius: 6, background: activeSection === section.id ? 'rgba(201,168,76,0.08)' : 'transparent' }}
              >{section.title}</div>
              {activeSection === section.id && section.articles.map(article => (
                <div
                  key={article.title}
                  onClick={() => setActiveArticle(article.title)}
                  style={{ fontSize: 12, color: activeArticle === article.title ? '#c8dff0' : '#475569', cursor: 'pointer', padding: '3px 8px 3px 16px', marginBottom: 2, borderLeft: `2px solid ${activeArticle === article.title ? GOLD : 'transparent'}` }}
                >{article.title}</div>
              ))}
            </div>
          ))}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 16, marginTop: 8 }}>
            <div style={{ fontSize: 12, color: '#334155', marginBottom: 8 }}>Need more help?</div>
            <a href="mailto:support@castellan-security.com" style={{ fontSize: 12, color: GOLD, textDecoration: 'none' }}>support@castellan-security.com</a>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, color: '#334155', marginBottom: 8 }}>{currentSection.title.replace(/^[^ ]+ /, '')} → {currentArticle.title}</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#f1f5f9', marginBottom: 24, lineHeight: 1.2 }}>{currentArticle.title}</h1>
          <div style={{ background: PANEL, border: '1px solid rgba(255,255,255,0.05)', borderRadius: 12, padding: '24px 28px' }}>
            {renderContent(currentArticle.content)}
          </div>

          {/* Next article */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
            {(() => {
              const allArticles = sections.flatMap(s => s.articles.map(a => ({ ...a, sectionId: s.id })))
              const idx = allArticles.findIndex(a => a.title === activeArticle)
              const prev = allArticles[idx - 1]
              const next = allArticles[idx + 1]
              return (
                <>
                  {prev ? (
                    <button onClick={() => { setActiveSection(prev.sectionId); setActiveArticle(prev.title) }}
                      style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#64748b', cursor: 'pointer', fontSize: 13 }}>
                      ← {prev.title}
                    </button>
                  ) : <div />}
                  {next && (
                    <button onClick={() => { setActiveSection(next.sectionId); setActiveArticle(next.title) }}
                      style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#64748b', cursor: 'pointer', fontSize: 13 }}>
                      {next.title} →
                    </button>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      </div>
    </div>
  )
}
